import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { logout } from '../../store/slices/authSlice';
import LanguageSwitcher from '../LanguageSwitcher';
import {
  AppBar, Toolbar, Typography, Button, IconButton, Box, Container,
  Badge, Menu, MenuItem, Avatar, Drawer, List, ListItem,
  ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme
} from '@mui/material';
import {
  ShoppingCart, Notifications, Person,
  Menu as MenuIcon, Chat, Dashboard, SmartToy
} from '@mui/icons-material';

const MainLayout = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const hideFooter = location.pathname === '/ai-assistant';
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { unreadCount } = useSelector((state) => state.notifications);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
    setAnchorEl(null);
  };

  const getDashboardLink = () => {
    if (!user) return '/login';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'supplier_employee': return '/supplier';
      case 'customer': return '/customer';
      default: return '/';
    }
  };

  // Apply user's preferred language after login
  useEffect(() => {
    const pref = user?.preferences?.language;
    if (pref && pref !== i18n.language) {
      i18n.changeLanguage(pref);
      localStorage.setItem('osesg_lang', pref);
    }
  }, [user, i18n]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Top Bar */}
      <AppBar position="sticky" sx={{ bgcolor: '#1a237e' }}>
        <Container maxWidth="xl">
          <Toolbar sx={{ gap: 2 }}>
            {isMobile && (
              <IconButton color="inherit" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}

            {/* Logo */}
            <Typography
              variant="h5" component={Link} to="/"
              sx={{ fontWeight: 800, color: 'white', textDecoration: 'none', letterSpacing: 2 }}
            >
              OSESG
            </Typography>

            <Box sx={{ flex: 1 }} />

            {!isMobile && (
              <>
                <Button color="inherit" component={Link} to="/products">{t('products')}</Button>

                <LanguageSwitcher color="inherit" />

                {isAuthenticated ? (
                  <>
                    <IconButton color="inherit" component={Link} to="/ai-assistant" title={t('ai_assistant')}>
                      <SmartToy />
                    </IconButton>
                    <IconButton color="inherit" component={Link} to={`${getDashboardLink()}/chat`}>
                      <Chat />
                    </IconButton>
                    <IconButton color="inherit">
                      <Badge badgeContent={unreadCount} color="secondary">
                        <Notifications />
                      </Badge>
                    </IconButton>
                    <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#ff6f00' }}>
                        {user?.firstName?.charAt(0)}
                      </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                      <MenuItem disabled>
                        <Typography variant="body2" fontWeight={600}>
                          {user?.firstName} {user?.lastName}
                        </Typography>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => { navigate(getDashboardLink()); setAnchorEl(null); }}>
                        <ListItemIcon><Dashboard fontSize="small" /></ListItemIcon>
                        {t('dashboard')}
                      </MenuItem>
                      <MenuItem onClick={() => { navigate(`${getDashboardLink()}/profile`); setAnchorEl(null); }}>
                        <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                        {t('profile')}
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    <Button color="inherit" component={Link} to="/login">{t('login')}</Button>
                    <Button variant="contained" color="secondary" component={Link} to="/register">
                      {t('register')}
                    </Button>
                  </>
                )}
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 280, pt: 2 }}>
          <Typography variant="h6" sx={{ px: 2, pb: 2, fontWeight: 800, color: '#1a237e' }}>
            OSESG
          </Typography>
          <Divider />
          <List>
            <ListItem button component={Link} to="/products" onClick={() => setMobileOpen(false)}>
              <ListItemText primary={t('products')} />
            </ListItem>
            {isAuthenticated ? (
              <>
                <ListItem button component={Link} to="/ai-assistant" onClick={() => setMobileOpen(false)}>
                  <ListItemText primary={t('ai_assistant')} />
                </ListItem>
                <ListItem button component={Link} to={getDashboardLink()} onClick={() => setMobileOpen(false)}>
                  <ListItemText primary={t('dashboard')} />
                </ListItem>
                <ListItem button onClick={handleLogout}>
                  <ListItemText primary={t('logout')} />
                </ListItem>
              </>
            ) : (
              <>
                <ListItem button component={Link} to="/login" onClick={() => setMobileOpen(false)}>
                  <ListItemText primary={t('login')} />
                </ListItem>
                <ListItem button component={Link} to="/register" onClick={() => setMobileOpen(false)}>
                  <ListItemText primary={t('register')} />
                </ListItem>
              </>
            )}
            <Divider />
            <ListItem>
              <LanguageSwitcher variant="text" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Outlet />
      </Box>

      {/* Footer — hidden on the AI Assistant page so the chat gets the full remaining height */}
      {!hideFooter && (
        <Box component="footer" sx={{ bgcolor: '#1a237e', color: 'white', py: 4, mt: 4 }}>
          <Container maxWidth="xl">
            <Box display="flex" flexWrap="wrap" gap={4}>
              <Box flex={1} minWidth={250}>
                <Typography variant="h6" fontWeight={800} gutterBottom>OSESG</Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Global B2B Managed Trade Platform. Connecting businesses worldwide with trusted suppliers.
                </Typography>
              </Box>
              <Box flex={1} minWidth={200}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Quick Links</Typography>
                <Typography variant="body2" component={Link} to="/products" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5, textDecoration: 'none' }}>Products</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>About Us</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>Contact</Typography>
              </Box>
              <Box flex={1} minWidth={200}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>Support</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>Help Center</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>Terms of Service</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>Privacy Policy</Typography>
              </Box>
            </Box>
            <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.2)' }} />
            <Typography variant="body2" textAlign="center" sx={{ opacity: 0.6 }}>
              © {new Date().getFullYear()} OSESG. All rights reserved.
            </Typography>
          </Container>
        </Box>
      )}
    </Box>
  );
};

export default MainLayout;
